"use client";

import AuthNotice from "@/components/auth/AuthNotice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { authNotice, parseAuthError, type AuthMessage } from "@/lib/auth-errors";
import { useApi } from "@/lib/pariette";
import { Clock3, Loader2, LockKeyhole, ShieldCheck, Smartphone } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

/**
 * Backend 423 yanıtı (demcas docs/security.md): süreli kilit + kalan süre +
 * uygunsa "kilidi kendim açayım" bileti. SMS kodu ya da push onayıyla açılır.
 * 5 Eki 2026: başlık/mesaj backend'in kodlu yanıtından (8426401/8426402),
 * hatalar AuthNotice ile kod + ref gösterir.
 */
export type SecurityLock = {
  title?: string;
  message?: string;
  error_code?: number;
  ref?: string;
  retry_after?: number | null;
  release_mode?: "auto" | "owner_sms" | "admin";
  unlock_available?: boolean;
  unlock_channel?: "sms" | "push" | null;
  unlock_ticket?: string | null;
};

type Step = "idle" | "starting" | "sms" | "push" | "confirming" | "done";

const mmss = (s: number) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

export default function SecurityLockPanel({ lock, onReleased }: { lock: SecurityLock; onReleased: () => void }) {
  const t = useTranslations("auth");
  const { $post, $get } = useApi(true);
  const [remaining, setRemaining] = useState<number | null>(lock.retry_after ?? null);
  const [step, setStep] = useState<Step>("idle");
  const [code, setCode] = useState("");
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<AuthMessage | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (remaining === null || remaining <= 0) return;
    const id = setInterval(() => setRemaining((r) => (r !== null && r > 0 ? r - 1 : r)), 1000);
    return () => clearInterval(id);
  }, [remaining]);

  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current);
    },
    [],
  );

  // Kilit kalkınca panel kendini kapatır; giriş sayfası "Giriş Yap"ı geri
  // getirir ve şifre alanına odaklanır ("Tekrar giriş yap" ara adımı yok).
  const finish = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setStep("done");
    setError(null);
    onReleased();
  };

  const fail = (e: any) => setError(parseAuthError(e, t));

  const start = async () => {
    if (!lock.unlock_ticket) return;
    setStep("starting");
    setError(null);
    try {
      const res: any = await $post("auth/self-unlock/start", { ticket: lock.unlock_ticket });
      if (res?.released) return finish();
      if (res?.channel === "sms") {
        setStep("sms");
        setInfo(t("securityUnlockSmsSent", { phone: res.phone ?? "" }));
        return;
      }
      setStep("push");
      setInfo(t("securityUnlockPushSent"));
      pollRef.current = setInterval(async () => {
        try {
          const st: any = await $get(`auth/self-unlock/status/${lock.unlock_ticket}`);
          if (st?.released) finish();
        } catch (e: any) {
          if (pollRef.current) clearInterval(pollRef.current);
          setStep("idle");
          fail(e);
        }
      }, 2000);
    } catch (e: any) {
      setStep("idle");
      fail(e);
    }
  };

  const confirm = async () => {
    if (!lock.unlock_ticket || code.trim() === "") return;
    setStep("confirming");
    setError(null);
    try {
      const res: any = await $post("auth/self-unlock/confirm", { ticket: lock.unlock_ticket, code: code.trim() });
      if (res?.released) return finish();
      setStep("sms");
    } catch (e: any) {
      setStep("sms");
      fail(e);
    }
  };

  const expired = remaining !== null && remaining <= 0;

  useEffect(() => {
    if (expired) onReleased();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expired]);

  const lockNotice: AuthMessage = {
    ...authNotice("error", lock.title ?? t("securityLockTitle"), lock.message),
    code: lock.error_code && lock.error_code > 8426000 ? lock.error_code : undefined,
    ref: lock.ref,
  };

  return (
    <div className="space-y-3">
      <AuthNotice notice={lockNotice}>
        {remaining !== null && !expired && (
          <p className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
            <Clock3 className="size-3.5" aria-hidden />
            {t("securityLockRemaining")} <span className="font-mono tabular-nums">{mmss(remaining)}</span>
          </p>
        )}
      </AuthNotice>

      {lock.unlock_available && !expired && step !== "done" && (
        <div className="space-y-3 rounded-md border p-4">
          {(step === "idle" || step === "starting") && (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <LockKeyhole className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                <p className="text-sm text-muted-foreground">{t("securityUnlockIntro")}</p>
              </div>
              <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={start} disabled={step === "starting"}>
                {step === "starting" ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}
                {t("securityUnlockSelf")}
              </Button>
            </div>
          )}

          {info && step !== "idle" && step !== "starting" && <p className="text-sm">{info}</p>}

          {(step === "sms" || step === "confirming") && (
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                confirm();
              }}
            >
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                aria-label={t("securityUnlockCodeLabel")}
                placeholder={t("securityUnlockCodeLabel")}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="h-11 flex-1 text-center font-mono text-lg tracking-[0.4em] placeholder:font-sans placeholder:text-sm placeholder:tracking-normal"
                autoFocus
              />
              <Button type="submit" className="h-11 shrink-0" disabled={step === "confirming" || code.trim() === ""}>
                {step === "confirming" && <Loader2 className="size-4 animate-spin" />}
                {t("securityUnlockConfirm")}
              </Button>
            </form>
          )}

          {step === "push" && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Smartphone className="size-4" aria-hidden />
              <Loader2 className="size-4 animate-spin" aria-hidden />
              {t("securityUnlockWaitPush")}
            </p>
          )}
        </div>
      )}

      {error && <AuthNotice notice={error} />}
    </div>
  );
}
